/**
 * Clerk headless email/OTP + SSO auth component.
 *
 * Uses Clerk's custom flow API (`useSignUp` / `useSignIn`) for:
 *   - New users: signUp.create() → prepareEmailAddressVerification() → attemptEmailAddressVerification()
 *   - Existing users: signIn.create() → prepareFirstFactor() → attemptFirstFactor()
 *   - Google/Apple SSO: signUp.authenticateWithRedirect({ strategy: 'oauth_google' })
 *
 * When `signUp.create()` returns `form_identifier_exists`, the component
 * transparently falls back to sign-in so returning users can still log in
 * with email/OTP.
 *
 * Reuses the `AccountCreate` UI component from `@nextday/funnel-ui` so the
 * visual experience matches Kinde funnels. The `skipValidation` prop is used
 * in OTP phase to bypass email regex validation.
 */
import { useSignUp, useSignIn, useSession } from "@clerk/clerk-react";
import { buildClerkSsoCallbackUrl, buildClerkSsoCompleteUrl } from "@src/auth/ClerkSsoCallbackPage";
import { AccountCreate, OtpVerification } from "@nextday/funnel-ui";
import type { AccountCreateData } from "@nextday/survey-kit";
import { useCallback, useEffect, useRef, useState } from "react";

type OtpPhase = "email" | "otp";
type AuthMode = "signup" | "signin";

interface ClerkApiError {
  code: string;
  longMessage?: string;
}

const isClerkErrorWithCode = (err: unknown, code: string): err is { errors: ClerkApiError[] } =>
  err !== null &&
  typeof err === "object" &&
  "errors" in err &&
  Array.isArray((err as { errors: unknown[] }).errors) &&
  (err as { errors: ClerkApiError[] }).errors.some((e) => e.code === code);

const extractClerkMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "errors" in err) {
    const firstMsg = (err as { errors: Array<{ longMessage?: string }> }).errors[0]?.longMessage;
    return firstMsg ? String(firstMsg) : fallback;
  }
  return fallback;
};

const AuthSpinner = () => (
  <div className="flex flex-col items-center gap-3 p-6" role="status" aria-label="Resuming">
    <div className="size-8 animate-spin rounded-full border-2 border-white/20 border-t-white" />
  </div>
);

export const ClerkEmailAuth = ({
  data,
  onAdvance,
  onAnswer,
}: {
  data: AccountCreateData;
  onAdvance: () => void;
  onAnswer: (answerKey: string, input: { value: unknown; action?: string }) => void;
}) => {
  const { signUp, isLoaded: isSignUpLoaded, setActive } = useSignUp();
  const { signIn, isLoaded: isSignInLoaded } = useSignIn();
  const { session } = useSession();
  const hasAdvancedRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [phase, setPhase] = useState<OtpPhase>("email");
  const authModeRef = useRef<AuthMode>("signup");

  const isLoaded = isSignUpLoaded && isSignInLoaded;

  const advanceOnce = useCallback(() => {
    if (hasAdvancedRef.current) return;
    hasAdvancedRef.current = true;
    onAdvance();
  }, [onAdvance]);

  useEffect(() => {
    if (session && !hasAdvancedRef.current) {
      onAnswer(data.answerKey ?? "account_create", {
        value: session.id,
        action: "session_restored",
      });
      advanceOnce();
    }
  }, [session, advanceOnce, onAnswer, data.answerKey]);

  const startSignIn = useCallback(
    async (email: string): Promise<void> => {
      if (!signIn) throw new Error("signIn not loaded");
      authModeRef.current = "signin";
      const result = await signIn.create({ identifier: email });
      const emailFactor = result.supportedFirstFactors?.find(
        (f): f is Extract<typeof f, { strategy: "email_code" }> => f.strategy === "email_code",
      );
      if (!emailFactor || !("emailAddressId" in emailFactor)) {
        throw new Error("Email OTP not available for this account");
      }
      await signIn.prepareFirstFactor({
        strategy: "email_code",
        emailAddressId: emailFactor.emailAddressId,
      });
    },
    [signIn],
  );

  const handleEmailSubmit = useCallback(
    (email: string): void => {
      if (!isLoaded || !signUp) return;
      setSubmitting(true);
      setError(undefined);
      onAnswer(data.answerKey ?? "account_create", {
        value: email,
        action: "submit_email",
      });

      void (async () => {
        try {
          await signUp.create({ emailAddress: email });
          await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
          authModeRef.current = "signup";
          setPhase("otp");
        } catch (err: unknown) {
          if (isClerkErrorWithCode(err, "form_identifier_exists")) {
            try {
              await startSignIn(email);
              setPhase("otp");
            } catch (signInErr: unknown) {
              setError(extractClerkMessage(signInErr, "Something went wrong. Please try again."));
            }
          } else {
            setError(extractClerkMessage(err, "Something went wrong. Please try again."));
          }
        } finally {
          setSubmitting(false);
        }
      })();
    },
    [isLoaded, signUp, startSignIn, onAnswer, data.answerKey],
  );

  const handleOtpSubmit = useCallback(
    (code: string): void => {
      if (!isLoaded || !setActive) return;
      setSubmitting(true);
      setError(undefined);

      void (async () => {
        try {
          let sessionId: string | undefined;

          if (authModeRef.current === "signin") {
            if (!signIn) return;
            const result = await signIn.attemptFirstFactor({ strategy: "email_code", code });
            if (result.status === "complete") sessionId = result.createdSessionId ?? undefined;
          } else {
            if (!signUp) return;
            const result = await signUp.attemptEmailAddressVerification({ code });
            if (result.status === "complete") sessionId = result.createdSessionId ?? undefined;
          }

          if (sessionId) {
            await setActive({ session: sessionId });
            onAnswer(data.answerKey ?? "account_create", {
              value: sessionId,
              action: "verified",
            });
            advanceOnce();
          } else {
            setError("Verification incomplete. Please try again.");
          }
        } catch (err: unknown) {
          setError(extractClerkMessage(err, "Invalid code. Please try again."));
        } finally {
          setSubmitting(false);
        }
      })();
    },
    [isLoaded, signUp, signIn, setActive, onAnswer, data.answerKey, advanceOnce],
  );

  const handleSocial = useCallback(
    (provider: "google" | "apple"): void => {
      if (!isSignUpLoaded || !signUp) return;
      onAnswer(data.answerKey ?? "account_create", {
        value: provider,
        action: "sso_redirect",
      });
      const strategy = provider === "google" ? "oauth_google" : "oauth_apple";
      void signUp.authenticateWithRedirect({
        strategy,
        redirectUrl: buildClerkSsoCallbackUrl(),
        redirectUrlComplete: buildClerkSsoCompleteUrl(),
      });
    },
    [isSignUpLoaded, signUp, onAnswer, data.answerKey],
  );

  if (!isLoaded) return <AuthSpinner />;

  if (session) return <AuthSpinner />;

  if (phase === "otp") {
    return (
      <OtpVerification
        question="Enter verification code"
        description="We sent a 6-digit code to your email."
        eyebrow={data.eyebrow}
        ctaLabel="Verify"
        length={6}
        isSubmitting={submitting}
        errorMessage={error}
        onSubmitOtp={handleOtpSubmit}
      />
    );
  }

  const ssoProviders = data.ssoProviders ?? [];

  return (
    <>
      <AccountCreate
        key="email"
        question={data.question}
        description={data.description}
        eyebrow={data.eyebrow}
        portraitUrl={data.portraitUrl}
        prefilledEmail={data.prefilledEmail}
        emailPlaceholder={data.emailPlaceholder}
        ctaLabel={data.ctaLabel}
        termsUrl={data.termsUrl}
        privacyUrl={data.privacyUrl}
        isSubmitting={submitting}
        errorMessage={error}
        onSubmitEmail={handleEmailSubmit}
        onContinueWithGoogle={
          ssoProviders.includes("google") ? () => handleSocial("google") : undefined
        }
        onContinueWithApple={
          ssoProviders.includes("apple") ? () => handleSocial("apple") : undefined
        }
        ssoPosition={data.ssoPosition}
        separatorLabel={data.separatorLabel}
        inlineCta={data.inlineCta}
        disclaimer={data.disclaimer}
      />
      {/* Mount point for Clerk's Smart CAPTCHA widget (bot protection). */}
      <div id="clerk-captcha" />
    </>
  );
};
